// contexts/CountryContext.tsx
import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { CountryConfig, CountryCode, ContractorCountryProfile } from "@/types/country";
import { CountryService } from "@/services/countryService";
import { useAuth } from "@/hooks/useAuth";

interface CountryContextType {
  // Current selected/active country
  currentCountry: CountryConfig | null;
  currentCountryCode: CountryCode;
  
  // All available countries
  availableCountries: CountryConfig[];
  
  // Current contractor's country profile
  contractorProfile: ContractorCountryProfile | null;
  
  // Loading states
  isLoading: boolean;
  isLoadingCountries: boolean;
  isLoadingProfile: boolean;
  
  // Methods
  setCurrentCountryCode: (code: CountryCode) => void;
  refreshCountries: () => Promise<void>;
  refreshContractorProfile: (contractorId?: string) => Promise<void>;
  
  // Utility methods
  getCurrencySymbol: () => string;
  getDateFormat: () => string;
  formatCurrency: (amount: number) => string;
  formatDate: (date: Date | string) => string;
}

export const CountryContext = React.createContext<CountryContextType | undefined>(
  undefined,
);

interface CountryProviderProps {
  children: React.ReactNode;
  defaultCountryCode?: CountryCode;
  contractorId?: string;
}

export const CountryProvider: React.FC<CountryProviderProps> = ({
  children,
  defaultCountryCode = 'GB', // Default to UK for new visitors
  contractorId,
}) => {
  const { user } = useAuth();
  const [availableCountries, setAvailableCountries] = useState<CountryConfig[]>([]);
  const [currentCountryCode, setCurrentCountryCode] = useState<CountryCode>(defaultCountryCode);
  const [contractorProfile, setContractorProfile] = useState<ContractorCountryProfile | null>(null);
  const [isLoadingCountries, setIsLoadingCountries] = useState(true);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [hasPerformedIpLookup, setHasPerformedIpLookup] = useState(false);

  // Get current country config
  const currentCountry = useMemo(() => {
    return availableCountries.find(country => country.code === currentCountryCode) || null;
  }, [availableCountries, currentCountryCode]);

  // Fetch available countries
  const refreshCountries = async () => {
    setIsLoadingCountries(true);
    try {
      const countries = await CountryService.getAllConfigs();
      
      if (countries && countries.length > 0) {
        setAvailableCountries(countries);
      } else {
        throw new Error('No enabled countries found');
      }
    } catch (error) {
      console.error("Failed to fetch country configurations:", error);
      
      // Set sensible fallback countries if all else fails
      const fallbackCountries = [
        {
          code: 'GB',
          name: 'United Kingdom',
          currencyCode: 'GBP',
          currencySymbol: '£',
          dateFormat: 'DD/MM/YYYY',
          addressSchema: [],
          onboardingSteps: [],
          enabled: true,
        },
        {
          code: 'US',
          name: 'United States',
          currencyCode: 'USD',
          currencySymbol: '$',
          dateFormat: 'MM/DD/YYYY',
          addressSchema: [],
          onboardingSteps: [],
          enabled: true,
        }
      ];
      
      setAvailableCountries(fallbackCountries);
    } finally {
      setIsLoadingCountries(false);
    }
  };

  // Fetch contractor's country profile
  const refreshContractorProfile = async (id?: string) => {
    setIsLoadingProfile(true);
    try {
      console.log('[CountryContext] Fetching contractor profile...');
      const profile = await CountryService.getCountryProfile();
      console.log('[CountryContext] Received profile:', profile);
      console.log('[CountryContext] Current country code before update:', currentCountryCode);
      setContractorProfile(profile);
      
      // Update current country code based on profile for logged-in users
      if (profile && profile.countryCode) {
        console.log(`[CountryContext] Profile country code: ${profile.countryCode}, Current: ${currentCountryCode}`);
        
        if (profile.countryCode !== currentCountryCode) {
          console.log(`[CountryContext] Updating country code from ${currentCountryCode} to ${profile.countryCode}`);
          setCurrentCountryCode(profile.countryCode as CountryCode);
        } else {
          console.log(`[CountryContext] Country code already matches profile (${currentCountryCode}), no change needed`);
        }
      } else if (profile) {
        console.log('[CountryContext] Profile exists but has no countryCode field, keeping current country code');
        console.log('[CountryContext] Profile object keys:', Object.keys(profile));
      } else {
        console.log('[CountryContext] No profile received, keeping current country code');
      }
    } catch (error) {
      console.error("[CountryContext] Failed to fetch contractor country profile:", error);
      setContractorProfile(null);
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    // Helper function to perform IP lookup and set country
    const performIpLookup = async () => {
      // Prevent lookup if already done, or if user is logged in (profile takes precedence)
      if (sessionStorage.getItem('countryCode') || user || hasPerformedIpLookup) {
        console.log('[CountryContext] IP lookup skipped:', {
          hasSessionStorage: !!sessionStorage.getItem('countryCode'),
          isUserLoggedIn: !!user,
          hasPerformedLookup: hasPerformedIpLookup
        });
        return;
      }

      try {
        console.log('[CountryContext] Performing IP-based country lookup...');
        const countryCode = await CountryService.getCountryFromIP();

        if (countryCode) {
          console.log(`[CountryContext] IP lookup successful, detected country: ${countryCode}`);
          setCurrentCountryCode(countryCode);
          sessionStorage.setItem('countryCode', countryCode);
        } else {
          console.log('[CountryContext] IP lookup returned no supported country, defaulting to GB');
        }
      } catch (error) {
        console.error('[CountryContext] IP lookup error:', error);
        // Silently fail and default to 'GB'
      } finally {
        setHasPerformedIpLookup(true);
      }
    };

    // Main effect logic
    const savedCountry = sessionStorage.getItem('countryCode') as CountryCode;
    if (savedCountry) {
      setCurrentCountryCode(savedCountry);
    }

    refreshCountries();
    
    if (contractorId) {
      refreshContractorProfile(contractorId);
    } else {
      performIpLookup();
    }
  }, [contractorId, user, hasPerformedIpLookup]);

  // Handle manual country selection
  const handleSetCurrentCountryCode = (code: CountryCode) => {
    setCurrentCountryCode(code);
    if (!user) {
      sessionStorage.setItem('countryCode', code);
    }
  };

  // Utility methods
  const getCurrencySymbol = (): string => {
    return currentCountry?.currencySymbol || '£';
  };

  const getDateFormat = (): string => {
    return currentCountry?.dateFormat || 'DD/MM/YYYY';
  };

  const formatCurrency = (amount: number): string => {
    const symbol = getCurrencySymbol();
    const currencyCode = currentCountry?.currencyCode || 'GBP';
    
    try {
      // Use Intl.NumberFormat for proper currency formatting
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: currencyCode,
      }).format(amount);
    } catch (error) {
      // Fallback to simple formatting
      return `${symbol}${amount.toFixed(2)}`;
    }
  };

  const formatDate = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const format = getDateFormat();
    
    // Simple date formatting based on country format
    if (format === 'MM/DD/YYYY') {
      // US format
      return dateObj.toLocaleDateString('en-US');
    } else {
      // UK format (DD/MM/YYYY)
      return dateObj.toLocaleDateString('en-GB');
    }
  };

  const contextValue: CountryContextType = {
    currentCountry,
    currentCountryCode,
    availableCountries,
    contractorProfile,
    isLoading: isLoadingCountries || isLoadingProfile,
    isLoadingCountries,
    isLoadingProfile,
    setCurrentCountryCode: handleSetCurrentCountryCode,
    refreshCountries,
    refreshContractorProfile,
    getCurrencySymbol,
    getDateFormat,
    formatCurrency,
    formatDate,
  };

  return (
    <CountryContext.Provider value={contextValue}>
      {children}
    </CountryContext.Provider>
  );
};

export const useCountry = (): CountryContextType => {
  const context = useContext(CountryContext);
  if (!context) {
    throw new Error("useCountry must be used within a CountryProvider");
  }
  return context;
};

export default CountryContext; 